import HiddenInput from "@/components/form/components/HiddenInput";
import { Injector } from "@/components/form/Injector";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, Form, Input, Modal, Row, Space } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateCommissionsBatch } from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { CommissionsBatchStep } from "../../enums";
import { CommissionsBatchList, UpdateCommissionsBatch } from "../../types";
import { isCommissionAdvanceBatch } from "../../utils";

interface Props {
  batch?: CommissionsBatchList;
  open: boolean;
  onFormCancel: VoidFunction;
}

export const UpdateCommissionsBatchForm = ({ batch, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<UpdateCommissionsBatch>();
  const namePrefix = Form.useWatch("name", form)?.substring(0, 10);

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const updateCommissionsBatch = useUpdateCommissionsBatch();

  useEffect(() => {
    if (open && batch) {
      form.setFieldsValue({
        optimisticLockVersion: batch.optimisticLockVersion,
        name: batch.name,
        closesCommissionAdvanceMonth: batch.closesCommissionAdvanceMonth,
      });
    }
  }, [open, batch, form]);

  const handleFormSubmit = () => {
    if (batch) {
      form
        .validateFields()
        .then((values) =>
          updateCommissionsBatch.mutate(
            { id: batch.id, object: values },
            {
              onSuccess: () => {
                onFormCancel();
              },
              onError: (error) => setErrorsToForm(form, "commissions.batch.attrs", error.violations),
            },
          ),
        )
        .catch(resolveFormValidationError);
    }
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={open}
      title={t("commissions.batch.titles.updateBatch")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updateCommissionsBatch.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="updateCommissionsBatchForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={t("commissions.batch.attrs.name")}
              rules={[validations.size(1, 64), validations.notBlank]}
              normalize={(value) => [namePrefix, value ?? ""].join("-")}
              getValueProps={(value) => ({ value: value?.substring(11) })}
            >
              <Injector>
                {(props) => (
                  <Space.Compact block>
                    <Space.Addon className="nowrap">{namePrefix}</Space.Addon>

                    <Input placeholder={t("commissions.batch.helpers.batchNamePlaceholder")} {...props} />
                  </Space.Compact>
                )}
              </Injector>
            </Form.Item>
          </Col>
        </Row>

        {batch && isCommissionAdvanceBatch(batch.type, isTopAgentPlus) && (
          <Row gutter={rowGutter}>
            <Col span={24}>
              <Form.Item name="closesCommissionAdvanceMonth" valuePropName="checked" rules={[validations.none]}>
                <Checkbox disabled={batch.step !== CommissionsBatchStep.IMPORT}>
                  <LabelWithTooltip
                    label={t("commissions.batch.attrs.closesCommissionAdvanceMonth")}
                    tooltip={
                      batch.step === CommissionsBatchStep.IMPORT
                        ? t("commissions.batch.helpers.closesCommissionAdvanceMonthDesc")
                        : t("commissions.batch.helpers.closesCommissionAdvanceMonthChangeableOnlyInImportStep")
                    }
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
};
